import { useState } from 'react'
import useBodyScrollLock from '../../hooks/useBodyScrollLock'
import './AuthModal.css'

/* ── Icônes SVG ── */
const IconLogin = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/>
    <line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
)
const IconRegister = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/>
    <line x1="22" y1="11" x2="16" y2="11"/>
  </svg>
)
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
)
const IconEyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
)
const IconWarning = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
)
const IconCheck = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const IconX = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)
const IconSend = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)

/* ── Logique force du mot de passe ── */
const passRules = [
  { id: 'len',     label: 'Au moins 8 caractères',       test: (p) => p.length >= 8 },
  { id: 'upper',   label: 'Une lettre majuscule (A-Z)',   test: (p) => /[A-Z]/.test(p) },
  { id: 'lower',   label: 'Une lettre minuscule (a-z)',   test: (p) => /[a-z]/.test(p) },
  { id: 'digit',   label: 'Un chiffre (0-9)',             test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'Un caractère spécial (!@#$…)', test: (p) => /[^A-Za-z0-9]/.test(p) },
]

function getStrength(pass) {
  if (!pass) return 0
  return passRules.filter(r => r.test(pass)).length
}

function StrengthBar({ pass }) {
  const score  = getStrength(pass)
  const labels = ['', 'Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort']
  const colors = ['', '#e63946', '#f59e0b', '#f59e0b', '#4caf50', '#22c55e']
  if (!pass) return null
  return (
    <div className="auth-strength">
      <div className="auth-strength-bars">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="auth-strength-bar" style={{ background: i <= score ? colors[score] : 'var(--border)' }} />
        ))}
      </div>
      <span className="auth-strength-label" style={{ color: colors[score] }}>{labels[score]}</span>
    </div>
  )
}

function PassRules({ pass }) {
  if (!pass) return null
  if (passRules.every(r => r.test(pass))) return null
  return (
    <ul className="auth-pass-rules">
      {passRules.map(r => {
        const ok = r.test(pass)
        return (
          <li key={r.id} className={`auth-pass-rule ${ok ? 'rule-ok' : 'rule-fail'}`}>
            {ok ? <IconCheck /> : <IconX />} {r.label}
          </li>
        )
      })}
    </ul>
  )
}

/* ── Indicatifs pays ── */
const COUNTRY_CODES = [
  { code: '+237', iso: 'cm', name: 'CM' },
  { code: '+33',  iso: 'fr', name: 'FR' },
  { code: '+1',   iso: 'us', name: 'US' },
  { code: '+44',  iso: 'gb', name: 'GB' },
  { code: '+49',  iso: 'de', name: 'DE' },
  { code: '+32',  iso: 'be', name: 'BE' },
  { code: '+41',  iso: 'ch', name: 'CH' },
  { code: '+34',  iso: 'es', name: 'ES' },
  { code: '+39',  iso: 'it', name: 'IT' },
  { code: '+351', iso: 'pt', name: 'PT' },
  { code: '+212', iso: 'ma', name: 'MA' },
  { code: '+213', iso: 'dz', name: 'DZ' },
  { code: '+216', iso: 'tn', name: 'TN' },
  { code: '+221', iso: 'sn', name: 'SN' },
  { code: '+225', iso: 'ci', name: 'CI' },
  { code: '+242', iso: 'cg', name: 'CG' },
  { code: '+243', iso: 'cd', name: 'CD' },
  { code: '+241', iso: 'ga', name: 'GA' },
  { code: '+27',  iso: 'za', name: 'ZA' },
  { code: '+234', iso: 'ng', name: 'NG' },
  { code: '+254', iso: 'ke', name: 'KE' },
  { code: '+55',  iso: 'br', name: 'BR' },
  { code: '+52',  iso: 'mx', name: 'MX' },
  { code: '+86',  iso: 'cn', name: 'CN' },
  { code: '+81',  iso: 'jp', name: 'JP' },
]

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000'

export default function AuthModal({
  isOpen         = false,
  onClose        = () => {},
  onUserLogin    = () => {},
  onUserRegister = () => {},
}) {
  // Tabs : 'login' | 'register' | 'forgot'
  const [authTab, setAuthTab] = useState('login')

  // ── Login ──
  const [lEmail,    setLEmail]    = useState('')
  const [lPass,     setLPass]     = useState('')
  const [lShowPass, setLShowPass] = useState(false)
  const [lErr,      setLErr]      = useState('')
  const [lLoading,  setLLoading]  = useState(false)
  const [lSuccess,  setLSuccess]  = useState('')

  // ── Register ──
  const [rName,       setRName]       = useState('')
  const [rEmail,      setREmail]      = useState('')
  const [rDialCode,   setRDialCode]   = useState('+237')
  const [rDialOpen,   setRDialOpen]   = useState(false)
  const [rPhone,      setRPhone]      = useState('')
  const [rAddress,    setRAddress]    = useState('')
  const [rCity,       setRCity]       = useState('')
  const [rPass,       setRPass]       = useState('')
  const [rPassConf,   setRPassConf]   = useState('')
  const [rShowPass,   setRShowPass]   = useState(false)
  const [rShowConf,   setRShowConf]   = useState(false)
  const [rErr,        setRErr]        = useState('')
  const [rPassTouched,setRPassTouched]= useState(false)
  const [rLoading,    setRLoading]    = useState(false)

  // ── Mot de passe oublié ──
  const [forgotEmail,   setForgotEmail]   = useState('')
  const [forgotSent,    setForgotSent]    = useState(false)
  const [forgotErr,     setForgotErr]     = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)

  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  const passOk = passRules.every(r => r.test(rPass))

  const resetAll = () => {
    setLEmail(''); setLPass(''); setLShowPass(false); setLErr(''); setLSuccess('')
    setRName(''); setREmail(''); setRPhone(''); setRDialCode('+237')
    setRAddress(''); setRCity(''); setRPass(''); setRPassConf('')
    setRShowPass(false); setRShowConf(false); setRErr(''); setRPassTouched(false)
    setForgotEmail(''); setForgotSent(false); setForgotErr('')
    setAuthTab('login')
  }

  const handleClose = () => { resetAll(); onClose() }

  /* ── Connexion ── */
  const handleLogin = async () => {
    setLErr('')
    if (!lEmail.trim() || !lPass.trim()) { setLErr('Veuillez remplir tous les champs.'); return }
    setLLoading(true)
    try {
      const res  = await fetch(`${API}/api/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: lEmail, password: lPass }),
      })
      const data = await res.json()
      if (!res.ok) { setLErr(data.message || 'Erreur de connexion'); return }
      // Stocker le token JWT
      localStorage.setItem('token', data.token)
      onUserLogin(data.user)
      resetAll()
    } catch {
      setLErr('Erreur réseau — vérifiez votre connexion.')
    } finally {
      setLLoading(false)
    }
  }

  /* ── Inscription ── */
  const handleRegister = async () => {
    setRErr('')
    setRPassTouched(true)
    if (!rName.trim() || !rEmail.trim() || !rPhone.trim() || !rAddress.trim() || !rCity.trim() || !rPass.trim() || !rPassConf.trim()) {
      setRErr('Tous les champs sont obligatoires.'); return
    }
    if (!passOk) { setRErr('Le mot de passe ne respecte pas les conditions requises.'); return }
    if (rPass !== rPassConf) { setRErr('Les mots de passe ne correspondent pas.'); return }
    const phoneDigits = rPhone.replace(/\D/g, '')
    if (phoneDigits.length < 6) { setRErr('Numéro de téléphone invalide.'); return }

    setRLoading(true)
    try {
      const res  = await fetch(`${API}/api/auth/register`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          name:     rName,
          email:    rEmail,
          password: rPass,
          phone:    `${rDialCode} ${rPhone}`,
          address:  rAddress,
          city:     rCity,
        }),
      })
      const data = await res.json()
      if (!res.ok) { setRErr(data.message || "Erreur lors de l'inscription"); return }
      const savedEmail = rEmail
      resetAll()
      setAuthTab('login')
      setLEmail(savedEmail)
      setLSuccess('Compte créé avec succès ! Connectez-vous maintenant.')
    } catch (err) {
      console.error('❌ Erreur réseau inscription', err)
      setRErr('Erreur réseau — vérifiez votre connexion.')
    } finally {
      setRLoading(false)
    }
  }

  /* ── Mot de passe oublié ── */
  const handleForgot = async () => {
    setForgotErr('')
    if (!forgotEmail.trim()) { setForgotErr('Veuillez entrer votre adresse email.'); return }
    setForgotLoading(true)
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: forgotEmail }),
      })
      // On affiche toujours le succès pour ne pas révéler si l'email existe
      setForgotSent(true)
    } catch {
      setForgotSent(true)
    } finally {
      setForgotLoading(false)
    }
  }

  const headerSub = {
    login:    'Connectez-vous à votre compte',
    register: 'Créez votre compte gratuitement',
    forgot:   'Réinitialisez votre mot de passe',
  }

  return (
    <div className="auth-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="auth-card">

        {/* ── Header ── */}
        <div className="auth-header">
          <div className="auth-header-icon">
            <img src={require('../../assets/monlogo.jpeg')} alt="Logo" className="auth-logo-img"
              onError={(e) => { e.target.style.display = 'none' }} />
          </div>
          <div className="auth-header-title">Mon compte</div>
          <div className="auth-header-sub">{headerSub[authTab]}</div>
          <button className="auth-close" onClick={handleClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="auth-body">

          {/* ── Tabs (masqués sur forgot) ── */}
          {authTab !== 'forgot' && (
            <div className="auth-tabs">
              <button className={`auth-tab${authTab === 'login' ? ' active' : ''}`}
                onClick={() => { setAuthTab('login'); setLErr(''); setRErr(''); setLSuccess('') }}>
                <IconLogin /> Connexion
              </button>
              <button className={`auth-tab${authTab === 'register' ? ' active' : ''}`}
                onClick={() => { setAuthTab('register'); setLErr(''); setRErr(''); setLSuccess('') }}>
                <IconRegister /> Inscription
              </button>
            </div>
          )}

          {/* ══ CONNEXION ══ */}
          {authTab === 'login' && (
            <>
              {lSuccess && (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'var(--green-lt)', color:'var(--green-dk)', fontSize:13, fontWeight:600, borderRadius:9, marginBottom:10 }}>
                  ✅ {lSuccess}
                </div>
              )}
              {lErr && <div className="auth-err"><IconWarning /> {lErr}</div>}
              <div className="auth-field">
                <label className="auth-label">Email <span className="auth-req">*</span></label>
                <input className="auth-input" type="email" placeholder="votre@email.fr"
                  value={lEmail} onChange={(e) => setLEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()} autoFocus required />
              </div>
              <div className="auth-field">
                <label className="auth-label">Mot de passe <span className="auth-req">*</span></label>
                <div className="auth-pass-wrap">
                  <input className="auth-input" type={lShowPass ? 'text' : 'password'} placeholder="••••••••"
                    value={lPass} onChange={(e) => setLPass(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()} required />
                  <button className="auth-eye" type="button" onClick={() => setLShowPass(s => !s)}>
                    {lShowPass ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </div>

              {/* Mot de passe oublié */}
              <div className="auth-forgot-link">
                <span onClick={() => { setAuthTab('forgot'); setLErr('') }}>
                  Mot de passe oublié ?
                </span>
              </div>

              <button className="auth-submit" onClick={handleLogin} disabled={lLoading}>
                {lLoading ? 'Connexion…' : <><IconLogin /> Se connecter</>}
              </button>
              <div className="auth-switch">
                Pas encore de compte ? <span onClick={() => { setAuthTab('register'); setLErr('') }}>S'inscrire</span>
              </div>
            </>
          )}

          {/* ══ INSCRIPTION ══ */}
          {authTab === 'register' && (
            <>
              {rErr && <div className="auth-err"><IconWarning /> {rErr}</div>}

              <div className="auth-field">
                <label className="auth-label">Nom complet <span className="auth-req">*</span></label>
                <input className="auth-input" type="text" placeholder="Jean Dupont"
                  value={rName} onChange={(e) => setRName(e.target.value)} required />
              </div>

              <div className="auth-field">
                <label className="auth-label">Email <span className="auth-req">*</span></label>
                <input className="auth-input" type="email" placeholder="votre@email.fr"
                  value={rEmail} onChange={(e) => setREmail(e.target.value)} required />
              </div>

              <div className="auth-field">
                <label className="auth-label">Téléphone <span className="auth-req">*</span></label>
                <div className="auth-phone-wrap">
                  <div className="auth-dial-wrap">
                    <button type="button" className="auth-dial-btn" onClick={() => setRDialOpen(o => !o)}>
                      {(() => { const c = COUNTRY_CODES.find(x => x.code === rDialCode); return c ? (
                        <>
                          <img src={`https://flagcdn.com/20x15/${c.iso}.png`} alt={c.name} className="auth-dial-flag-img" onError={e => { e.target.style.display='none' }} />
                          <span className="auth-dial-name">{c.name}</span>
                          <span className="auth-dial-code">{c.code}</span>
                        </>
                      ) : null })()}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {rDialOpen && (
                      <div className="auth-dial-dropdown">
                        {COUNTRY_CODES.map(c => (
                          <button key={c.code+c.name} type="button"
                            className={"auth-dial-option" + (c.code === rDialCode ? " active" : "")}
                            onClick={() => { setRDialCode(c.code); setRDialOpen(false) }}>
                            <img src={`https://flagcdn.com/20x15/${c.iso}.png`} alt={c.name} className="auth-dial-flag-img" onError={e => { e.target.style.display='none' }} />
                            <span className="auth-dial-opt-name">{c.name}</span>
                            <span className="auth-dial-opt-code">{c.code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input className="auth-input auth-phone-input" type="tel" placeholder="6 00 00 00 00"
                    value={rPhone} onChange={(e) => setRPhone(e.target.value)} required />
                </div>
              </div>

              <div className="auth-field">
                <label className="auth-label">Adresse <span className="auth-req">*</span></label>
                <input className="auth-input" type="text" placeholder="12 rue des Fleurs"
                  value={rAddress} onChange={(e) => setRAddress(e.target.value)} required />
              </div>

              <div className="auth-field">
                <label className="auth-label">Ville <span className="auth-req">*</span></label>
                <input className="auth-input" type="text" placeholder="Paris"
                  value={rCity} onChange={(e) => setRCity(e.target.value)} required />
              </div>

              <div className="auth-field">
                <label className="auth-label">Entrer un mot de passe <span className="auth-req">*</span></label>
                <div className="auth-pass-wrap">
                  <input className="auth-input" type={rShowPass ? 'text' : 'password'} placeholder="Créez un mot de passe fort"
                    value={rPass}
                    onChange={(e) => { setRPass(e.target.value); setRPassTouched(true) }} required />
                  <button className="auth-eye" type="button" onClick={() => setRShowPass(s => !s)}>
                    {rShowPass ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
                <StrengthBar pass={rPass} />
                {rPassTouched && <PassRules pass={rPass} />}
              </div>

              <div className="auth-field">
                <label className="auth-label">Confirmer le mot de passe <span className="auth-req">*</span></label>
                <div className="auth-pass-wrap">
                  <input className="auth-input" type={rShowConf ? 'text' : 'password'} placeholder="Confirmer le mot de passe"
                    value={rPassConf} onChange={(e) => setRPassConf(e.target.value)} required />
                  <button className="auth-eye" type="button" onClick={() => setRShowConf(s => !s)}>
                    {rShowConf ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
                {rPassConf && rPass !== rPassConf && (
                  <div className="auth-pass-nomatch">Les mots de passe ne correspondent pas</div>
                )}
              </div>

              <button className="auth-submit" onClick={handleRegister} disabled={rLoading}>
                {rLoading ? 'Création…' : <><IconRegister /> Créer mon compte</>}
              </button>
              <div className="auth-switch">
                Déjà un compte ? <span onClick={() => { setAuthTab('login'); setRErr('') }}>Se connecter</span>
              </div>
            </>
          )}

          {/* ══ MOT DE PASSE OUBLIÉ ══ */}
          {authTab === 'forgot' && (
            <>
              {forgotSent ? (
                <div className="auth-forgot-success">
                  <div className="auth-forgot-icon"><IconCheck /></div>
                  <div className="auth-forgot-title">Email envoyé !</div>
                  <div className="auth-forgot-text">
                    Si <strong>{forgotEmail}</strong> est associé à un compte,
                    vous recevrez un lien de réinitialisation valable <strong>10 minutes</strong>.
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--slate)' }}>
                    Pensez à vérifier vos spams.
                  </div>
                </div>
              ) : (
                <>
                  {forgotErr && <div className="auth-err"><IconWarning /> {forgotErr}</div>}
                  <div className="auth-forgot-intro">
                    Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">Email <span className="auth-req">*</span></label>
                    <input className="auth-input" type="email" placeholder="votre@email.fr"
                      value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleForgot()} autoFocus />
                  </div>
                  <button className="auth-submit" onClick={handleForgot} disabled={forgotLoading}>
                    {forgotLoading ? 'Envoi…' : <><IconSend /> Envoyer le lien</>}
                  </button>
                </>
              )}
              <div className="auth-switch" style={{ marginTop: 14 }}>
                <span onClick={() => { setAuthTab('login'); setForgotSent(false); setForgotEmail(''); setForgotErr('') }}>
                  ← Retour à la connexion
                </span>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  )
}